import { css } from 'styled-components';
import { PxToRem, PX_TO_REM, SIZES } from '../sizes/sizes';
import { CSSProperties } from 'react';
import { BreakpointSize, MEDIA_QUERY, mediaQueries } from '../breakpoints/breakpoints';
import { IGutterProps } from '../spaces/space';

export const FONT_WEIGHTS = {
  DEFAULT: 'default',
  EXTRA_LIGHT: 'extraLight',
  REGULAR: 'regular',
  MEDIUM: 'medium',
  SEMI_BOLD: 'semiBold',
  EXTRA_BOLD: 'extraBold'
} as const;

export type FontWeights = typeof FONT_WEIGHTS;
export type FontWeight = FontWeights[keyof FontWeights];

export const fontWeights: Record<FontWeight, number> = {
  [FONT_WEIGHTS.DEFAULT]: 400,
  [FONT_WEIGHTS.EXTRA_LIGHT]: 200,
  [FONT_WEIGHTS.REGULAR]: 400,
  [FONT_WEIGHTS.MEDIUM]: 500,
  [FONT_WEIGHTS.SEMI_BOLD]: 600,
  [FONT_WEIGHTS.EXTRA_BOLD]: 800
};

export const FONT_SIZES = {
  DISPLAY_LARGE: 'displayLarge',
  DISPLAY_SMALL: 'displaySmall',
  HEADING_1: 'heading1',
  HEADING_2: 'heading2',
  HEADING_3: 'heading3',
  HEADING_4: 'heading4',
  HEADING_5: 'heading5',
  HEADING_5_MOBILE: 'heading5Mobile',
  HEADING_6: 'heading6',
  TEXT_LARGE: 'textLarge',
  TEXT_DEFAULT: 'textDefault',
  TEXT_SMALL: 'textSmall',
  TEXT_MICRO: 'textMicro',
  TEXT_NANO: 'textNano',
  TEXT_FEMTO: 'textFemto'
} as const;

export type FontSizes = typeof FONT_SIZES;
export type FontSize = FontSizes[keyof FontSizes];

export const fontSizes: Record<FontSize, PxToRem> = {
  [FONT_SIZES.DISPLAY_LARGE]: PX_TO_REM[52],
  [FONT_SIZES.DISPLAY_SMALL]: PX_TO_REM[44],
  [FONT_SIZES.HEADING_1]: PX_TO_REM[40],
  [FONT_SIZES.HEADING_2]: PX_TO_REM[36],
  [FONT_SIZES.HEADING_3]: PX_TO_REM[32],
  [FONT_SIZES.HEADING_4]: PX_TO_REM[28],
  [FONT_SIZES.HEADING_5]: PX_TO_REM[24],
  [FONT_SIZES.HEADING_5_MOBILE]: PX_TO_REM[20], // Original heading6 value
  [FONT_SIZES.HEADING_6]: PX_TO_REM[18],
  [FONT_SIZES.TEXT_LARGE]: PX_TO_REM[18],
  [FONT_SIZES.TEXT_DEFAULT]: PX_TO_REM[16],
  [FONT_SIZES.TEXT_SMALL]: PX_TO_REM[14],
  [FONT_SIZES.TEXT_MICRO]: PX_TO_REM[12],
  [FONT_SIZES.TEXT_NANO]: PX_TO_REM[10],
  [FONT_SIZES.TEXT_FEMTO]: PX_TO_REM[8]
};

export const lineHeightSizes: Record<FontSize, PxToRem> = {
  [FONT_SIZES.DISPLAY_LARGE]: PX_TO_REM[56],
  [FONT_SIZES.DISPLAY_SMALL]: PX_TO_REM[48],
  [FONT_SIZES.HEADING_1]: PX_TO_REM[48],
  [FONT_SIZES.HEADING_2]: PX_TO_REM[44],
  [FONT_SIZES.HEADING_3]: PX_TO_REM[40],
  [FONT_SIZES.HEADING_4]: PX_TO_REM[36],
  [FONT_SIZES.HEADING_5]: PX_TO_REM[32],
  [FONT_SIZES.HEADING_5_MOBILE]: PX_TO_REM[28], // Original heading6 value
  [FONT_SIZES.HEADING_6]: PX_TO_REM[24],
  [FONT_SIZES.TEXT_LARGE]: PX_TO_REM[28],
  [FONT_SIZES.TEXT_DEFAULT]: PX_TO_REM[24],
  [FONT_SIZES.TEXT_SMALL]: PX_TO_REM[20],
  [FONT_SIZES.TEXT_MICRO]: PX_TO_REM[20],
  [FONT_SIZES.TEXT_NANO]: PX_TO_REM[18],
  [FONT_SIZES.TEXT_FEMTO]: PX_TO_REM[12]
};

export interface IFontWeightProps {
  fontWeightCss?: FontWeight;
}

export interface IFontSizes {
  fontSizes?: FontSizeByMediaQueries;
}

export const FONT_FAMILIES = {
  DEFAULT: 'default',
  MARKETING: 'marketing'
} as const;

type FontFamilies = typeof FONT_FAMILIES;
export type FontFamily = FontFamilies[keyof FontFamilies];

export const fontFamilies = {
  [FONT_FAMILIES.DEFAULT]: 'Inter, sans-serif',
  [FONT_FAMILIES.MARKETING]: 'Modern Era, sans-serif'
};

export interface IMarketingFontProps {
  useMarketingFont?: boolean;
}

export interface INoLineHeightProps {
  noExtraLineHeight?: boolean;
}

type FontStyles = 'normal' | 'italic';
type ModernEraFontTypes = 'Bold' | 'BoldItalic' | 'ExtraBold' | 'ExtraBoldItalic' | 'Regular' | 'Italic' | 'Light' | 'LightItalic' | 'Medium' | 'MediumItalic';
type NeueHaasGroteskFontTypes = 'Roman' | 'Italic' | 'Medium';

const useModernEraFontFace = (baseAssetsUrl: string, fontType: ModernEraFontTypes, fontWeight: FontWeight, fontStyle: FontStyles) => css`
  @font-face {
    font-family: 'Modern Era';
    src: ${() => `url('${baseAssetsUrl}/f/ModernEra-${fontType}.eot');`}; /* IE9 Compat Modes */
    src: ${() => `url('${baseAssetsUrl}/f/ModernEra-${fontType}.eot?#iefix') format('embedded-opentype'),
      url('${baseAssetsUrl}/f/ModernEra-${fontType}.woff') format('woff'), /* IE6-IE8 */ 
      url('${baseAssetsUrl}/f/ModernEra-${fontType}.svg#ModernEra-${fontType}') format('svg'); /* Modern Browsers */ /* Legacy iOS */`};
    font-weight: ${() => fontWeights[fontWeight]};
    font-style: ${() => fontStyle};
  }
`;

const useInterFontFace = (baseAssetsUrl: string, fontWeight: FontWeight) => css`
  @font-face {
    font-family: 'Inter';
    font-style: normal;
    font-display: swap;
    font-weight: ${() => fontWeights[fontWeight]};
    src: ${() => `url('${baseAssetsUrl}/f/i-${fontWeights[fontWeight]}.woff2') format('woff2'),
        url('${baseAssetsUrl}/f/i-${fontWeights[fontWeight]}.woff') format('woff');`};
  }
`;

const useNeueHaasGrotesk = (baseAssetsUrl: string, size: number, fontType: NeueHaasGroteskFontTypes, fontStyle: FontStyles) => css`
  @font-face {
    font-family: 'Neue Haas Grotesk Display Pro';
    src: ${() => `url('${baseAssetsUrl}/f/Linotype - Neue Haas Grotesk Display Pro ${size} ${fontType}.ttf');`};
    font-style: ${() => fontStyle};
  }
`;

export const useFonts = (baseAssetsUrl: string) => css`
  ${useModernEraFontFace(baseAssetsUrl, 'Bold', FONT_WEIGHTS.SEMI_BOLD, 'normal')};
  ${useModernEraFontFace(baseAssetsUrl, 'BoldItalic', FONT_WEIGHTS.SEMI_BOLD, 'italic')}
  ${useModernEraFontFace(baseAssetsUrl, 'ExtraBold', FONT_WEIGHTS.EXTRA_BOLD, 'normal')};
  ${useModernEraFontFace(baseAssetsUrl, 'ExtraBoldItalic', FONT_WEIGHTS.SEMI_BOLD, 'italic')};
  ${useModernEraFontFace(baseAssetsUrl, 'Italic', FONT_WEIGHTS.REGULAR, 'italic')};
  ${useModernEraFontFace(baseAssetsUrl, 'Light', FONT_WEIGHTS.EXTRA_LIGHT, 'normal')};
  ${useModernEraFontFace(baseAssetsUrl, 'LightItalic', FONT_WEIGHTS.EXTRA_LIGHT, 'italic')};
  ${useModernEraFontFace(baseAssetsUrl, 'Medium', FONT_WEIGHTS.MEDIUM, 'normal')};
  ${useModernEraFontFace(baseAssetsUrl, 'MediumItalic', FONT_WEIGHTS.MEDIUM, 'italic')};
  ${useModernEraFontFace(baseAssetsUrl, 'Regular', FONT_WEIGHTS.REGULAR, 'normal')};

  ${useInterFontFace(baseAssetsUrl, FONT_WEIGHTS.EXTRA_LIGHT)};
  ${useInterFontFace(baseAssetsUrl, FONT_WEIGHTS.DEFAULT)};
  ${useInterFontFace(baseAssetsUrl, FONT_WEIGHTS.MEDIUM)};
  ${useInterFontFace(baseAssetsUrl, FONT_WEIGHTS.SEMI_BOLD)};
  ${useInterFontFace(baseAssetsUrl, FONT_WEIGHTS.EXTRA_BOLD)};

  ${useNeueHaasGrotesk(baseAssetsUrl, 55, 'Roman', 'normal')};
  ${useNeueHaasGrotesk(baseAssetsUrl, 56, 'Italic', 'italic')};
  ${useNeueHaasGrotesk(baseAssetsUrl, 65, 'Medium', 'normal')};
`;

export const useDefaultFontCss = css`
  font-family: ${({ theme }) => theme.fonts.fontFamilies[FONT_FAMILIES.DEFAULT]};
  line-height: 1.3;
  color: ${({ theme }) => theme.colours.text.default};
`;

export const useMarketingFontCss = css<IMarketingFontProps>`
  ${({ theme, useMarketingFont }) =>
    useMarketingFont &&
    css`
      &,
      * {
        font-family: ${theme.fonts.fontFamilies[FONT_FAMILIES.MARKETING]};
      }
    `}
`;

export const useFontWeightCss = (fontWeight?: FontWeight | boolean) =>
  fontWeight !== false &&
  css<IFontWeightProps>`
    font-weight: ${({ theme, fontWeightCss = fontWeight }) => {
      const defaultFontWeight = fontWeightCss === undefined || fontWeightCss === true ? FONT_WEIGHTS.DEFAULT : fontWeightCss;
      return theme.fonts.fontWeights[defaultFontWeight];
    }};
  `;

export interface IDisplayType {
  asDisplay?: typeof FONT_SIZES.DISPLAY_LARGE | typeof FONT_SIZES.DISPLAY_SMALL;
}

export type FontSizeByMediaQueries = Partial<Record<BreakpointSize, FontSize>>;
export const generateFontsBasedByMediaQuery = (defaultFontSize: FontSize, fontSizeMapping: FontSizeByMediaQueries = {}, noExtraLineHeight?: boolean) =>
  Object.entries(fontSizeMapping)
    .map(([key, value]) =>
      defaultFontSize === value
        ? ``
        : `${mediaQueries[MEDIA_QUERY.MIN][key as BreakpointSize]} {
            font-size: ${fontSizes[value]};
            line-height: ${noExtraLineHeight ? 1 : lineHeightSizes[value]};
        }`
    )
    .join(``);

export const useFontSizeCss = (fontSizeMapping?: FontSizeByMediaQueries) => {
  const defaultFontSize =
    fontSizeMapping?.[SIZES.X_SMALL] ??
    fontSizeMapping?.[SIZES.SMALL] ??
    fontSizeMapping?.[SIZES.MEDIUM] ??
    fontSizeMapping?.[SIZES.LARGE] ??
    fontSizeMapping?.[SIZES.X_LARGE] ??
    fontSizeMapping?.[SIZES.XX_LARGE] ??
    fontSizeMapping?.[SIZES.XXX_LARGE] ??
    FONT_SIZES.TEXT_DEFAULT;

  return css<IDisplayType & INoLineHeightProps>`
    ${({ theme, asDisplay, noExtraLineHeight }) => css`
      font-size: ${theme.fonts.fontSizes[asDisplay ?? defaultFontSize]};
      line-height: ${noExtraLineHeight ? 1 : theme.fonts.lineHeightSizes[asDisplay ?? defaultFontSize]};
      ${!asDisplay && generateFontsBasedByMediaQuery(defaultFontSize, fontSizeMapping, noExtraLineHeight)};
    `}
  `;
};

export interface ITextAlignProps extends Pick<CSSProperties, 'textAlign'> {}

export const useTextAlignCss = css<ITextAlignProps>`
  ${({ textAlign }) =>
    textAlign &&
    css`
      text-align: ${textAlign};
    `}
`;

// Note: limit to only these for now
// properties will used together with `useGutterCss`
export enum DisplayCss {
  Inline = 'inline',
  InlineBlock = 'inline-block',
  Block = 'block'
}

export interface IDisplayCssProps {
  displayCss?: DisplayCss;
}

export const useDisplayCss = (defaultDisplay: DisplayCss = DisplayCss.Inline) => css<IDisplayCssProps>`
  ${({ displayCss }) =>
    displayCss &&
    defaultDisplay !== displayCss &&
    css`
      display: ${displayCss};
    `}
`;

export const useTypographyCss = css<IMarketingFontProps & ITextAlignProps>`
  ${useMarketingFontCss};
  ${useTextAlignCss};
`;

export interface ITypographyProps
  extends IFontWeightProps,
    IMarketingFontProps,
    IGutterProps,
    ITextAlignProps,
    IDisplayType,
    IDisplayCssProps,
    INoLineHeightProps {}

export const fonts = {
  fontSizes,
  lineHeightSizes,
  fontWeights,
  fontFamilies
};
